// noinspection JSUnresolvedReference

import React, {useContext, useEffect, useId, useMemo, useRef, useState} from "react";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import {ErpCalculationApi, ProjectPermission} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import moment from "moment-timezone";
import {Chart} from "chart.js";
import {getContrastBackgroundColors, getContrastBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {chartColors} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import Tooltip from "../../../../../components/Tooltip.jsx";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import {useHasPermission} from "../../../../../components/auth/rolesAndPermissions.js";
import {useRenderCurrencyValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";

export default function OrderPerformancePanel({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();
    let renderCurrencyValue = useRenderCurrencyValue();

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, textColor} = chartColors;

    let [overviewData, setOverviewData] = useState(null);
    let [overviewLoading, setOverviewLoading] = useState(true);

    let hasPermission = useHasPermission();

    let [userSettings] = useUserSettings();

    let selectedData = useMemo(() => {

        if(userSettings === null) { return {} };

        return {
            orderTurnover: userSettings?.isMarketing ? overviewData?.orderTurnoverGross : overviewData?.orderTurnoverNet,
            orderTurnoverGranular: userSettings?.isMarketing ? overviewData?.orderTurnoverGrossGranular : overviewData?.orderTurnoverNetGranular,
            orderProfit: userSettings?.isMarketing ? overviewData?.orderProfitGross : overviewData?.orderProfitNet,
            orderProfitGranular: userSettings?.isMarketing ? overviewData?.orderProfitGrossGranular : overviewData?.orderProfitNetGranular,
            orderAmount: userSettings?.isMarketing ? overviewData?.orderAmountGross : overviewData?.orderAmountNet,
            orderAmountGranular: userSettings?.isMarketing ? overviewData?.orderAmountGrossGranular : overviewData?.orderAmountNetGranular,
            orderAverage: userSettings?.isMarketing ? overviewData?.orderAverageGross : overviewData?.orderAverageNet,
            orderAverageGranular: userSettings?.isMarketing ? overviewData?.orderAverageGrossGranular : overviewData?.orderAverageNetGranular,
        };
    }, [overviewData, userSettings]);

    useEffect(() => {
        if (!project) {
            return;
        }

        let active = true;
        setOverviewLoading(true);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformance({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            })
            .then((nextOverviewData) => {
                if (!active) {
                    return;
                }

                setOverviewData(nextOverviewData);
                setOverviewLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                setOverviewLoading(false);
                et(e).then(toast.error);
            });

        return () => {
            active = false;
        };

    }, [project?.id, since, until, centresAllowListed, domainsAllowListed]);

    const chartOptions = {
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        scales: {
            y: {
                border: {
                    display: false,
                },
                ticks: {
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    color: darkMode ? gridColor.dark : gridColor.light,
                },
            },
            x: {
                border: {
                    display: false,
                },
                ticks: {
                    padding: 10,
                    align: 'center',
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    };

    let granularTurnoverId = useId();
    const granularTurnoverCanvas = useRef(null);

    let granularProfitId = useId();
    const granularProfitCanvas = useRef(null);

    let granularOrderAmountId = useId();
    const granularOrderAmountCanvas = useRef(null);

    let granularAverageTurnoverId = useId();
    const granularAverageTurnoverCanvas = useRef(null);

    useEffect(() => {
        if (overviewLoading || overviewData === null || userSettings === null) {
            return;
        }

        let labels = Object.keys(
            selectedData?.orderTurnoverGranular ?? []
        ).map((dateString) => moment(dateString).format('DD.MM.'));

        let createdCharts = [];

        let createChart = (canvasRef, configuration) => {
            if (canvasRef.current === null) {
                return null;
            }

            let chart = new Chart(canvasRef.current, configuration);
            createdCharts.push(chart);

            return chart;
        };

        // -- -- -------------------------------------------------------------------------------------------------------

        // -- -- -------------------------------------------------------------------------------------------------------

        let turnoverGranular = Object.values(selectedData?.orderTurnoverGranular ?? []);

        let turnoverChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.turnover'),
                    backgroundColor: getContrastBackgroundColors()[1],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[1],
                    data: turnoverGranular.map((moneyObject) => parseInt(moneyObject.amount) / 100),
                }
            ],
        };

        createChart(granularTurnoverCanvas, {
            type: 'bar',
            data: turnoverChartData,
            options: chartOptions
        });
        // -- -- -------------------------------------------------------------------------------------------------------

        let profitGranular = Object.values(selectedData?.orderProfitGranular ?? []);

        let profitChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.profit'),
                    backgroundColor: getContrastBackgroundColors()[6],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[6],
                    data: profitGranular.map((moneyObject) => parseInt(moneyObject.amount) / 100),
                }
            ],
        };

        createChart(granularProfitCanvas, {
            type: 'bar',
            data: profitChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------

        let orderAmountGranular = Object.values(selectedData?.orderAmountGranular ?? []);

        let granularOrderAmountChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.amount'),
                    backgroundColor: getContrastBackgroundColors()[3],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[3],
                    data: orderAmountGranular,
                }
            ],
        };

        createChart(granularOrderAmountCanvas, {
            type: 'bar',
            data: granularOrderAmountChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------

        let averageGranular = Object.values(selectedData?.orderAverageGranular ?? []);

        let granularAverageTurnoverChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.average-order-price'),
                    backgroundColor: getContrastBackgroundColors()[10],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[10],
                    data: averageGranular.map((moneyObject) => parseInt(moneyObject.amount) / 100),
                }
            ]
        };

        createChart(granularAverageTurnoverCanvas, {
            type: 'line',
            data: granularAverageTurnoverChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------
        return () => {
            createdCharts.forEach((chart) => chart.destroy());
        };

    }, [overviewData, overviewLoading, selectedData, userSettings]);

    let result = [];

    result.push(<LoadingComponent
        key={'pp-turnover'}
        loading={overviewLoading || !overviewData}
        className="comp__roundedBox flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
    >
        <header
            className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                {t('orders.turnover')}
            </h3>
            <Tooltip bg="dark" size="md" position="right" className={'inline-flex'}>
                <div className="text-xs text-gray-200">
                    {t('tooltip.order.turnover')}
                </div>
            </Tooltip>
        </header>
        <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
            <span
                className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
            >
                {renderCurrencyValue(selectedData?.orderTurnover, true)}
            </span>
        </div>
        <div className={'p-2'}>
            <div className="grow">
                <canvas ref={granularTurnoverCanvas} id={granularTurnoverId}></canvas>
            </div>
        </div>
    </LoadingComponent>);

    if (hasPermission(ProjectPermission.Profitability, project)) {
        result.push(<LoadingComponent
            key={'pp-profit'}
            loading={overviewLoading || !overviewData}
            className="comp__roundedBox flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('orders.profit')}
                </h3>
                <Tooltip bg="dark" size="md" position="right" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.order.profit')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
                <span
                    className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                >
                    {renderCurrencyValue(selectedData?.orderProfit, true)}
                </span>
            </div>
            <div className={'p-2'}>
                <div className="grow">
                    <canvas ref={granularProfitCanvas} id={granularProfitId}></canvas>
                </div>
            </div>
        </LoadingComponent>);
    }

    result.push(<LoadingComponent
        key={'pp-total'}
        loading={overviewLoading || !overviewData}
        className="comp__roundedBox flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
    >
        <header
            className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                {t('orders.orders-total')}
            </h3>
            <Tooltip bg="dark" size="md" position="bottom" className={'inline-flex'}>
                <div className="text-xs text-gray-200">
                    {t('tooltip.order.total')}
                </div>
            </Tooltip>
        </header>
        <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
            <span
                className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
            >
                    {selectedData?.orderAmount}
            </span>
        </div>
        <div className={'p-2'}>
            <div className="grow">
                <canvas ref={granularOrderAmountCanvas} id={granularOrderAmountId}></canvas>
            </div>
        </div>
    </LoadingComponent>);

    // If we have offline spends, we do not show the average order price on this overview...
    if (hasPermission(ProjectPermission.ProjectOfflineSpends, project) === false) {
        result.push(<LoadingComponent
            key={'pp-average'}
            loading={overviewLoading || !overviewData}
            className="comp__roundedBox flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"

        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('orders.average-order-price')}
                </h3>
                <Tooltip bg="dark" size="md" position="left" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.order.average-price')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
                <span
                    className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                >
                    {renderCurrencyValue(selectedData?.orderAverage, true)}
                </span>
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    <canvas ref={granularAverageTurnoverCanvas} id={granularAverageTurnoverId}></canvas>
                </div>
            </div>
        </LoadingComponent>);
    }

    return result;
}
